import CheckIcon from '@mui/icons-material/Check';
import NewReleases from '@mui/icons-material/NewReleases';
import {
    Button,
    Chip,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import {
    createStyles,
    makeStyles,
} from '@mui/styles';
import clsx from 'clsx';
import React,
{
    useCallback,
    useMemo,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import useSWR from 'swr';

import { UiBackgroundLogin } from '../../../common/UiBackground/UiBackgroundLogin';
import UiList from '../../../common/UiList/UiList';
import billboardBackground from '../../../images/billboardBackground.png';
import palette from '../../../theme/palette';
import { getSignInCMSData } from '../../../util/BillBoardCMS';
import { addUserBillBoard } from '../../../util/MarketoLookUp';

const useStyles = makeStyles(theme =>
    createStyles({
        center: {
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 4,
            justifyContent: 'center',
            alignSelf: 'center',
            paddingBottom: '7.5vh',
        },
        spotlight: {
            display: 'flex',
            flexDirection: 'row',
            margin: '0 0 10px 140px',
            justifyItems: 'center',
        },
        spotlightText: {
            paddingTop: '1px',
            fontWeight: 600,
            justifyItems: 'center',
        },
        container: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        },
        mainHeaderContainer: {
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
        },
        headerContainer: {
            display: 'flex',
            flexDirection: 'column',
        },
        paperDefault: {
            width: 420,
            flexShrink: 0,
        },
        title: {
            fontSize: '20px',
            fontWeight: 600,
            margin: '8px 0px 0',
        },
        description: {
            margin: '2px 0px 5px',
            fontSize: '14px',
        },
        chip: {
            margin: '2px 0px 15px 0',
            backgroundColor: theme.palette.info.light,
            width: '140px',
        },
        button: {
            width: '160px',
            marginLeft: '10px',
            alignSelf: 'center',
        },
        icon: { padding: '0 0 0 2pt' },
        iconAdjustments: {
            width: '25px',
            height: '25px',
            margin: '20px 10px 0 10px',
            borderLeft: 'solid',
            borderBottom: 'solid',
        },
        imageContainer: {
            display: 'flex',
            backgroundColor: theme.palette.primary.light,
            backgroundImage: `url(${billboardBackground})`,
            width: '100%',
            height: '175px',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '10px',
        },
        addMarketingForm: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            marginTop: '25px',
        },
        email: {
            width: '100%',
            visibility: 'visible',
            background: 'white',
        },
        interest: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            marginTop: '25px',
            alignSelf: 'center',
        },
        checkicon: {
            marginRight: '10px',
            color: theme.palette.primary.main,
        },
        list: { marginBottom: '10px' },
    }),
);

const BillboardComponent = () => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const [ email, updateEmail ] = useState('');
    const [ showForm, updateShowForm ] = useState(true);

    const { data } = useSWR('/',
        getSignInCMSData,
    );
    // take the first billboard from list
    const signInData = useMemo(() => {
        if (data) {
            return data.cloudPortalSignInContainer;
        }
    }, [ data ]);

    const bulletPoints = useMemo(() => {
        const bulletPointsList: string[] = [];
        if (signInData) {
            signInData.billboardBulletPoints.bulletPointListCollection.items.forEach((point: any) =>
                bulletPointsList.push(point.description));
        }
        return bulletPointsList;
    }, [ signInData ]);

    const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        updateEmail(e.target.value);
    }, []);

    const [ lastInteractionC, lastInteractionDetailsC ] = useMemo(() =>
        signInData ? [ signInData.lastInteractionC, signInData.lastInteractionDetailsC ] : [ '', '' ],
    [ signInData ]);

    const onSubmit = useCallback(
        async (emailText: string) => {
            if (!emailText) {
                return;
            }
            const response = await addUserBillBoard(emailText, lastInteractionC, lastInteractionDetailsC);
            if (response.success) {
                updateShowForm(false);
            }
        },
        [ lastInteractionC, lastInteractionDetailsC ],
    );

    return (
        <>
            {signInData &&
                <UiBackgroundLogin backgroundColor={palette.light.backgroundNew}>
                    <div className={classes.center}>
                        <div className={classes.spotlight}>
                            <Typography className={classes.spotlightText}>
                                {translate({ id: 'PRODUCT_SPOTLIGHT' })}
                            </Typography>
                            <NewReleases className={classes.icon} />
                        </div>
                        <Paper className={classes.paperDefault}>
                            <div className={classes.container}>
                                <div className={classes.mainHeaderContainer}>
                                    <span className={clsx(classes.iconAdjustments, 'material-icons-outlined')}>
                                        {signInData.billboardHeader.icon}
                                    </span>
                                    <div className={classes.headerContainer}>
                                        <div className={classes.title}>
                                            {signInData.billboardHeader.title}
                                        </div>
                                        <div className={classes.description}>
                                            {signInData.billboardHeader.description}
                                        </div>
                                        {signInData.billboardHeader.comingSoon &&
                                            <Chip
                                                label="Coming Soon"
                                                className={classes.chip}
                                                size="small" />}
                                    </div>
                                </div>
                                <div className={classes.imageContainer}>
                                    <img
                                        src={signInData.media.media.url}
                                        alt={translate({ id: 'BANNER_IMAGE_ALT' })}
                                    />
                                </div>
                                <div className={classes.list}>
                                    <UiList
                                        items={bulletPoints}
                                        colorListItemCheckmark="green"
                                    />
                                </div>
                            </div>
                        </Paper>
                        {showForm &&
                        <form className={classes.addMarketingForm}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                className={classes.email}
                                placeholder={translate({ id: 'EMAIL' })}
                                onChange={onChange}
                            />
                            <Button
                                variant="contained"
                                className={classes.button}
                                onClick={() => onSubmit(email)}>
                                {translate({ id: 'LEARN_MORE' })}
                            </Button>
                        </form>}
                        {!showForm &&
                            <div className={classes.interest}>
                                <CheckIcon className={classes.checkicon} />
                                <Typography>
                                    {translate({ id: 'THANKS_FOR_INTEREST' })}
                                </Typography>
                            </div>}
                    </div>
                </UiBackgroundLogin>}
        </>
    );
};

export default BillboardComponent;
